// Geolocation service for detecting user's city and showing relevant pages
export interface UserLocation {
  city: string;
  region: string;
  country: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  accuracy?: number;
}

export interface CityMapping {
  name: string;
  slug: string;
  region: string;
  aliases: string[];
  nearbyAirports: string[];
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

// UK Cities mapping for location detection
export const ukCityMappings: CityMapping[] = [
  {
    name: "Leicester",
    slug: "leicester",
    region: "East Midlands",
    aliases: ["leicestershire", "leicester city"],
    nearbyAirports: ["EMA", "BHX"],
    coordinates: { latitude: 52.6369, longitude: -1.1398 },
  },
  {
    name: "Birmingham",
    slug: "birmingham",
    region: "West Midlands",
    aliases: ["brum", "birmingham city"],
    nearbyAirports: ["BHX", "EMA"],
    coordinates: { latitude: 52.4862, longitude: -1.8904 },
  },
  {
    name: "Coventry",
    slug: "coventry",
    region: "West Midlands",
    aliases: ["coventry city"],
    nearbyAirports: ["BHX", "EMA"],
    coordinates: { latitude: 52.4068, longitude: -1.5197 },
  },
  {
    name: "Derby",
    slug: "derby",
    region: "East Midlands",
    aliases: ["derbyshire"],
    nearbyAirports: ["EMA", "BHX"],
    coordinates: { latitude: 52.9225, longitude: -1.4746 },
  },
  {
    name: "Nottingham",
    slug: "nottingham",
    region: "East Midlands",
    aliases: ["nottinghamshire"],
    nearbyAirports: ["EMA", "MAN"],
    coordinates: { latitude: 52.9548, longitude: -1.1581 },
  },
  {
    name: "London",
    slug: "london",
    region: "Greater London",
    aliases: ["greater london", "london city"],
    nearbyAirports: ["LHR", "LGW", "STN", "LTN", "LCY"],
    coordinates: { latitude: 51.5074, longitude: -0.1278 },
  },
  {
    name: "Manchester",
    slug: "manchester",
    region: "Greater Manchester",
    aliases: ["manchester city"],
    nearbyAirports: ["MAN", "LPL"],
    coordinates: { latitude: 53.4808, longitude: -2.2426 },
  },
  {
    name: "Liverpool",
    slug: "liverpool",
    region: "Merseyside",
    aliases: ["liverpool city"],
    nearbyAirports: ["LPL", "MAN"],
    coordinates: { latitude: 53.4084, longitude: -2.9916 },
  },
  {
    name: "Leeds",
    slug: "leeds",
    region: "West Yorkshire",
    aliases: ["leeds city"],
    nearbyAirports: ["LBA", "MAN"],
    coordinates: { latitude: 53.8008, longitude: -1.5491 },
  },
  {
    name: "Sheffield",
    slug: "sheffield",
    region: "South Yorkshire",
    aliases: ["sheffield city"],
    nearbyAirports: ["LBA", "MAN", "EMA"],
    coordinates: { latitude: 53.3811, longitude: -1.4701 },
  },
  {
    name: "Bristol",
    slug: "bristol",
    region: "South West England",
    aliases: ["bristol city"],
    nearbyAirports: ["BRS", "CWL"],
    coordinates: { latitude: 51.4545, longitude: -2.5879 },
  },
  {
    name: "Edinburgh",
    slug: "edinburgh",
    region: "Scotland",
    aliases: ["edinburgh city"],
    nearbyAirports: ["EDI", "GLA"],
    coordinates: { latitude: 55.9533, longitude: -3.1883 },
  },
  {
    name: "Glasgow",
    slug: "glasgow",
    region: "Scotland",
    aliases: ["glasgow city"],
    nearbyAirports: ["GLA", "EDI"],
    coordinates: { latitude: 55.8642, longitude: -4.2518 },
  },
  {
    name: "Cardiff",
    slug: "cardiff",
    region: "Wales",
    aliases: ["cardiff city"],
    nearbyAirports: ["CWL", "BRS"],
    coordinates: { latitude: 51.4816, longitude: -3.1791 },
  },
  {
    name: "Belfast",
    slug: "belfast",
    region: "Northern Ireland",
    aliases: ["belfast city"],
    nearbyAirports: ["BFS", "BHD"],
    coordinates: { latitude: 54.5973, longitude: -5.9301 },
  },
];

class GeolocationService {
  private static instance: GeolocationService;
  private cache: Map<string, UserLocation> = new Map();
  private readonly cacheExpiration = 24 * 60 * 60 * 1000; // 24 hours

  static getInstance(): GeolocationService {
    if (!GeolocationService.instance) {
      GeolocationService.instance = new GeolocationService();
    }
    return GeolocationService.instance;
  }

  // Calculate distance between two coordinates (in km)
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
  ): number {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // Find nearest UK city based on coordinates
  private findNearestCity(
    latitude: number,
    longitude: number,
  ): CityMapping | null {
    let nearest: CityMapping | null = null;
    let minDistance = Infinity;

    for (const city of ukCityMappings) {
      if (city.coordinates) {
        const distance = this.calculateDistance(
          latitude,
          longitude,
          city.coordinates.latitude,
          city.coordinates.longitude,
        );
        if (distance < minDistance) {
          minDistance = distance;
          nearest = city;
        }
      }
    }

    // Only return if within reasonable distance (100km)
    return minDistance < 100 ? nearest : null;
  }

  // Find city by name or alias
  private findCityByName(cityName: string): CityMapping | null {
    const name = cityName.toLowerCase().trim();
    return (
      ukCityMappings.find(
        (city) =>
          city.name.toLowerCase() === name ||
          city.slug === name ||
          city.aliases.some((alias) => alias.toLowerCase() === name),
      ) || null
    );
  }

  // Get user location using browser geolocation API
  // By default, this will NOT prompt for permission unless forcePrompt is true.
  async getUserLocationByGPS(
    forcePrompt: boolean = false,
  ): Promise<UserLocation | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      // If not explicitly forced, only proceed when permission is already granted
      const proceed = () => {
        const options = {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        } as const;

        navigator.geolocation.getCurrentPosition(
          (position) => {
            try {
              const { latitude, longitude, accuracy } = position.coords;
              try {
                localStorage.setItem("tigola_geo_consent", "granted");
              } catch {}
              const nearestCity = this.findNearestCity(latitude, longitude);

              if (nearestCity) {
                const location: UserLocation = {
                  city: nearestCity.name,
                  region: nearestCity.region,
                  country: "United Kingdom",
                  coordinates: { latitude, longitude },
                  accuracy,
                };
                resolve(location);
              } else {
                resolve(null);
              }
            } catch {
              resolve(null);
            }
          },
          (error) => {
            try {
              if ((error as any)?.code === 1 /* PERMISSION_DENIED */) {
                localStorage.setItem("tigola_geo_consent", "denied");
              }
            } catch {}
            console.warn(
              "Geolocation error:",
              (error as any)?.message || error,
            );
            resolve(null);
          },
          options,
        );
      };

      if (forcePrompt) {
        proceed();
        return;
      }

      try {
        const stored = localStorage.getItem("tigola_geo_consent");
        if (stored === "granted") {
          proceed();
          return;
        }
      } catch {}

      // Check Permissions API when available
      try {
        if (navigator.permissions && (navigator.permissions as any).query) {
          (navigator.permissions as any)
            .query({ name: "geolocation" as any })
            .then((status: any) => {
              if (status.state === "granted") {
                try {
                  localStorage.setItem("tigola_geo_consent", "granted");
                } catch {}
                proceed();
              } else {
                resolve(null);
              }
            })
            .catch(() => resolve(null));
          return;
        }
      } catch {}

      // No way to know permission state, avoid prompting automatically
      resolve(null);
    });
  }

  // Get user location using IP-based geolocation via ipapi.co (free, no API key required)
  async getUserLocationByIP(): Promise<UserLocation | null> {
    try {
      console.log("[Geolocation] Attempting IP-based geolocation via ipapi.co");

      let response: Response | null = null;
      try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          try {
            controller.abort();
          } catch (e) {
            // Ignore abort errors
          }
        }, 5000);

        try {
          console.log("[Geolocation] Making request to ipapi.co API");
          response = await fetch("https://ipapi.co/json/", {
            signal: controller.signal,
          });
          console.log(
            "[Geolocation] ipapi.co response status:",
            response.status,
          );
        } finally {
          clearTimeout(timeoutId);
        }
      } catch (fetchError) {
        console.log("[Geolocation] IP API fetch error:", fetchError);
        return null;
      }

      if (!response) {
        console.log("[Geolocation] No response from ipapi.co");
        return null;
      }

      if (!response.ok) {
        console.log("[Geolocation] IP API returned status:", response.status);
        return null;
      }

      let data: any;
      try {
        data = await response.json();
      } catch (parseError) {
        console.log(
          "[Geolocation] Failed to parse IP API response:",
          parseError,
        );
        return null;
      }

      console.log("[Geolocation] IP API response:", {
        city: data.city,
        country_code: data.country_code,
        latitude: data.latitude,
        longitude: data.longitude,
      });

      // Check if user is in UK
      if (data.country_code !== "GB") {
        console.log(
          "[Geolocation] User is not in UK (country:",
          data.country_code + ")",
        );
        return null;
      }

      // Try to match the city
      const city = this.findCityByName(data.city);
      if (city) {
        const location = {
          city: city.name,
          region: city.region,
          country: "United Kingdom",
          coordinates: city.coordinates,
        };
        console.log("[Geolocation] City found:", location);
        return location;
      }

      // If exact match not found, try coordinates if available
      if (data.latitude && data.longitude) {
        const nearestCity = this.findNearestCity(data.latitude, data.longitude);
        if (nearestCity) {
          const location = {
            city: nearestCity.name,
            region: nearestCity.region,
            country: "United Kingdom",
            coordinates: { latitude: data.latitude, longitude: data.longitude },
          };
          console.log("[Geolocation] Nearest city found:", location);
          return location;
        }
      }

      console.log("[Geolocation] No city match found for:", data.city);
      return null;
    } catch (error) {
      console.log("[Geolocation] IP geolocation error:", error);
      return null;
    }
  }

  // Main method to get user location (tries IP first, then GPS only if forced)
  // Set forcePrompt=true ONLY on explicit user action (e.g., a button click)
  async getUserLocation(
    forcePrompt: boolean = false,
  ): Promise<UserLocation | null> {
    const cacheKey = "user_location";
    const cached = this.getCachedLocation(cacheKey);
    if (cached) {
      console.log("[Geolocation] Using cached location:", cached);
      return cached;
    }

    console.log("[Geolocation] No cached location, attempting to detect");

    // Try IP-based geolocation first (no permission prompt, works globally)
    let location = await this.getUserLocationByIP();
    if (location) {
      console.log("[Geolocation] IP-based location detected:", location);
    }

    // Fallback to GPS only if explicitly forced or if IP geolocation failed
    if (!location && forcePrompt) {
      console.log(
        "[Geolocation] IP failed, trying GPS geolocation (explicit prompt)",
      );
      location = await this.getUserLocationByGPS(forcePrompt);
      if (location) {
        console.log("[Geolocation] GPS location detected:", location);
      }
    }

    // Cache the result
    if (location) {
      console.log("[Geolocation] Location detected and caching:", location);
      this.setCachedLocation(cacheKey, location);
    } else {
      console.log("[Geolocation] No location could be detected");
    }

    return location;
  }

  // Cache management
  private getCachedLocation(key: string): UserLocation | null {
    const cached = localStorage.getItem(`tigola_geo_${key}`);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < this.cacheExpiration) {
          return data;
        } else {
          localStorage.removeItem(`tigola_geo_${key}`);
        }
      } catch (error) {
        localStorage.removeItem(`tigola_geo_${key}`);
      }
    }
    return null;
  }

  private setCachedLocation(key: string, location: UserLocation): void {
    const cacheData = {
      data: location,
      timestamp: Date.now(),
    };
    localStorage.setItem(`tigola_geo_${key}`, JSON.stringify(cacheData));
  }

  // Get recommended city page based on user location
  getRecommendedCityPage(location: UserLocation | null): {
    citySlug: string;
    cityName: string;
    url: string;
    nearbyAirports: string[];
  } | null {
    if (!location) return null;

    const city = this.findCityByName(location.city);
    if (city) {
      return {
        citySlug: city.slug,
        cityName: city.name,
        url: `/taxi/${city.slug}`,
        nearbyAirports: city.nearbyAirports,
      };
    }

    return null;
  }

  // Clear cached location data
  clearCache(): void {
    const keys = Object.keys(localStorage).filter((key) =>
      key.startsWith("tigola_geo_"),
    );
    keys.forEach((key) => localStorage.removeItem(key));
  }
}

export const geolocationService = GeolocationService.getInstance();

import React from "react";

// Hook for React components
export function useGeolocation() {
  const [location, setLocation] = React.useState<UserLocation | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function detectLocation() {
      try {
        setLoading(true);
        // Non-intrusive by default in hooks
        const userLocation = await geolocationService.getUserLocation(false);
        setLocation(userLocation);
        setError(null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Location detection failed",
        );
      } finally {
        setLoading(false);
      }
    }

    detectLocation();
  }, []);

  return { location, loading, error };
}

export default geolocationService;
